<template>
	<div class="bg-white">
		<div class="container">
			<div class="page page-products">
				<breadcrumb :paths="breadcrumbPaths" :font-size="12" />
				<h3 class="title">Checkout</h3>
				<div class="row" v-if="isLoading">
					<div class="col-md-8 col-xs-12 mb-4">
						<shimmer
							width="100%"
							height="300px"
						/>

						<shimmer
							class="mt-4"
							width="100%"
							height="150px"
						/>

						<shimmer
							class="my-4"
							width="100%"
							height="500px"
						/>
					</div>
					<div class="col-md-4">
						<shimmer
							width="100%"
							height="300px"
						/>
					</div>
				</div>
				<div class="row" v-else-if="product && !isLoading">
					<div class="col-md-8 col-xs-12 mb-4">
						<div class="alert alert-primary mb-4" v-if="isOrderTypeBulky">
							<div class="row">
								<div class="col-auto">
									<i class="material-icons text-primary">info</i>
								</div>
								<div class="col pl-0">
									<div class="shipping-info-title">
										Informasi Ongkir
									</div>
									<div class="shipping-info-description">
										Informasi estimasi ongkir akan di input oleh admin
									</div>
								</div>
							</div>
						</div>
						<div class="card border-radius-8 shadow-box">
							<div class="card-header text-bold text-deep-black">
								Ringkasan Pesanan
							</div>
							<div class="card-body">
								<div class="row">
									<div class="col-sm-auto col-xs-12 d-flex justify-content-center">
										<div
											class="product-image"
											:style="productImage">
										</div>
									</div>
									<div class="col">
										<div class="product-name text-deep-black">{{ product.name }}</div>
										<div class="product-aggregate d-flex fs-12" ref="productAggregate">
											<li class="mr-3 first-aggregate">Berat <span>{{ product.weight }}gr / pcs</span></li>
											<li class="mr-3">Dikirim dari <span class="text-bold">{{ getShippingOrigin(product) }}</span></li>
										</div>
										<div class="price-color text-bold mt-2">
											{{ formatMoney(selectedPrice) }}
										</div>
										<div class="row mt-2">
											<div class="col-auto">
												<div class="input-group">
													<div class="input-group-prepend">
														<button
															type="button"
															class="btn btn-light qty-minus"
															:disabled="quantity <= 1"
															@click="quantity--"
														>
															<span>-</span>
														</button>
													</div>
													<input
														type="number"
														class="form-control radius-0 text-center w-90"
														v-model="quantity"
													/>
													<div class="input-group-append">
														<button
															type="button"
															class="btn btn-light qty-plus"
															:disabled="quantity >= 999999"
															@click="quantity++"
														>
															<span>+</span>
														</button>
													</div>
												</div>
											</div>
											<div class="col-auto d-flex align-items-center">
												<span>Stock:
													<span :class="{
														'text-danger': getDistributorStock(product) < 100
													}">
														{{ stockLabel(product) }}
													</span>
												</span>
											</div>
										</div>
										<div class="mt-2">
											<div class="mb-2">
												Harga Wholesale
											</div>
											<private-order-wholesale
												:wholesales="getDistributorWholesalePrice(product)"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div class="card mt-4 border-radius-8 shadow-box">
							<div class="card-header text-bold text-deep-black">
								Jenis Orderan
							</div>
							<div class="card-body">
								<div class="row">
									<div class="col-md-6 mb-2" v-for="(item, idx) in orderTypes" :key="idx">
										<div class="p-2 cursor-pointer border-grey border-radius-8" @click="orderType = item.type">
											<div class="row">
												<div class="col-auto d-flex align-items-center">
													<div class="form-check pl-4 d-flex justify-content-center">
														<input class="form-check-input position-static" type="radio" :value="item.type" v-model="orderType">
													</div>
												</div>
												<div class="col pl-0">
													<div>{{ item.label }}</div>
													<small>{{ item.description }}</small>
												</div>
												<div class="col-2 d-flex align-items-center px-1">
													<i :class="getIconClass(item)"></i>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div class="card mt-4 border-radius-8 shadow-box">
							<div class="card-header text-bold text-deep-black">
								Informasi Produk Order Bulky
							</div>
							<div class="card-body">
								<div class="form-group">
									<label class="text-bold text-deep-black">Nama Seller</label>
									<input
										class="form-control"
										:class="{ 'is-invalid': errorMessages.name }"
										type="text"
										placeholder="Masukkan Nama Anda..."
										v-model="name"
										@input="onInputName"
									/>
									<small class="text-danger" v-if="errorMessages.name">{{ errorMessages.name }}</small>
								</div>
								<div class="form-group">
									<label class="text-bold text-deep-black">Email</label>
									<input
										class="form-control"
										:class="{ 'is-invalid': errorMessages.email }"
										type="text"
										placeholder="Masukkan Email Anda..."
										v-model="email"
										@input="onInputEmail"
									/>
									<small class="text-danger" v-if="errorMessages.email">{{ errorMessages.email }}</small>
								</div>
								<div class="form-group">
									<label class="text-bold text-deep-black">Nomor Telepon</label>
									<div class="input-group">
										<div class="input-group-prepend">
											<span class="input-group-text" id="basic-addon1">+62</span>
										</div>
										<input
											class="form-control"
											:class="{ 'is-invalid': errorMessages.phone }"
											type="text"
											placeholder="Masukkan Nomor Telepon Anda..."
											v-model="phone"
											@input="onInputPhone"
										/>
									</div>
									<small class="text-danger" v-if="errorMessages.phone">{{ errorMessages.phone }}</small>
								</div>
								<div class="form-group" v-if="isOrderTypeBulky">
									<label class="text-bold text-deep-black">Kota / Kecamatan</label>
									<multiselect
										v-model="selectedSubdistrict"
										:class="{ 'multiselect-invalid': errorMessages.subdistrict }"
										:custom-label="cityLabel"
										:show-labels="false"
										track-by="subdistrict_id"
										placeholder="Masukkan Kota / Kecamatan (minimal 3 huruf)"
										:options="subdistricts"
										@search-change="fetchSubdistricts"
										@close="onCloseSubdistrict"
									>
									</multiselect>
									<small class="text-danger" v-if="errorMessages.subdistrict">{{ errorMessages.subdistrict }}</small>
								</div>
								<div class="form-group" v-if="isOrderTypeBulky">
									<label class="text-bold text-deep-black">Alamat Tujuan Seller</label>
									<textarea
										class="form-control"
										:class="{ 'is-invalid': errorMessages.address }"
										placeholder="Masukkan Alamat Lengkap..."
										rows="3"
										v-model="address"
										@input="onInputAddress"
									/>
									<small class="text-danger" v-if="errorMessages.address">{{ errorMessages.address }}</small>
								</div>
								<div class="form-group" v-if="isOrderTypeOutsource">
									<label class="text-bold text-deep-black">Upload Shipping Label</label>
									<div class="d-flex" v-if="shippingLabel">
										<i class="bi bi-file-earmark-text-fill text-primary fs-36"></i>
										<div class="ml-2 text-break">
											<div class="text-bold">
												<span class="mr-2">{{ shippingLabel.name }}</span>
												<i class="bi bi-trash text-danger cursor-pointer" @click="shippingLabel = null"></i>
											</div>
											<div>
												<small><i>Original Name : {{ shippingLabel.original_name }}</i></small>
											</div>
											<small>{{ shippingLabelSize }}</small>
										</div>
									</div>
									<drag-and-drop
										:is-error="errorMessages.shippingLabel.length > 0"
										:prefix="shippingLabelPrefix"
										@set-url="setUrl"
										v-if="!shippingLabel"
									/>
									<small class="text-danger" v-if="errorMessages.shippingLabel">{{ errorMessages.shippingLabel }}</small>
								</div>
							</div>
						</div>
						<div class="card mt-4 border-radius-8 shadow-box" v-if="isOrderTypeOutsource">
							<div class="card-header text-bold text-deep-black">
								Pembayaran
							</div>
							<div class="card-body p-0">
								<ul class="list-group list-group-flush border-radius-8">
									<li class="list-group-item cursor-pointer" v-for="(paymentChannel, idx) in availablePaymentChannels" :key="idx" @click="selectedPaymentChannel = paymentChannel.key">
										<div class="form-check d-flex align-items-center">
											<input class="form-check-input mt-0" type="radio" :value="paymentChannel.key" v-model="selectedPaymentChannel">
											<img
												class="payment-channel-icon mx-2"
												:src="getPaymentChannelIcon(paymentChannel.key)"
												alt="Icon"
											/>
											<div class="ml-2 text-bold text-deep-black">{{ paymentChannel.label }}</div>
										</div>
									</li>
								</ul>
							</div>
						</div>
					</div>
					<div class="col-md-4">
						<div class="card border-radius-8 sticky-payment-section shadow-box">
							<div class="card-header text-bold text-deep-black">
								Ringkasan Pembayaran
							</div>
							<div class="card-body">
								<div class="row mb-2">
									<div class="col-6">
										Total Harga ({{ quantity }} item{{ quantity > 1 ? 's' : '' }})
									</div>
									<div class="col-6 d-flex justify-content-end">
										{{ formatMoney(totalProductPrice) }}
									</div>
								</div>
								<div class="row mb-2" v-if="isIncludeOtherFee">
									<div class="col-6">
										Instant Payment fee
									</div>
									<div class="col-6 d-flex justify-content-end">
										{{ formatMoney(instantPaymentFee) }}
									</div>
								</div>
								<div class="row mb-2" v-if="isIncludeOtherFee">
									<div class="col-6">
										Instant Payment fee tax 11%
									</div>
									<div class="col-6 d-flex justify-content-end">
										{{ formatMoney(instantPaymentFeeTax) }}
									</div>
								</div>
							</div>
							<hr class="my-0" />
							<div class="card-body">
								<div class="row">
									<div class="col-6">
										<span class="grand-total">Total Tagihan</span>
									</div>
									<div class="col-6 d-flex justify-content-end">
										<div class="price-color grand-total">
											{{ formatMoney(totalPrice) }}
										</div>
									</div>
								</div>
							</div>
							<hr class="my-0" />
							<div class="p-4">
								<button
									type="button"
									class="btn btn-primary btn-block"
									:class="{ 'is-loading': isSubmit }"
									:disabled="isSubmit"
									@click="submit"
								>
									Bayar
								</button>
							</div>
						</div>
					</div>
				</div>
				<div class="not-found text-center pt-4" v-else-if="!product && !isLoading">
					<img src="/img/not_found.png" :width="250" alt="">
					<h3>Oops. . . Produk tidak ditemukan atau<br/>Anda tidak bisa mengakses produk ini!</h3>
					<button
						class="btn btn-primary"
						@click.prevent="back()"
					>
						{{ $t('Kembali') }}
					</button>
				</div>
			</div>
		</div>

		<main-footer />
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Axios from 'axios';
import MainFooter from '@/components/homepages/Footer.vue';
import DragAndDrop from '@/components/forms/DragAndDrop.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import Shimmer from '@/components/Shimmer.vue';
import PrivateOrderWholesale from '@/components/private-orders/detail/Wholesale.vue';
import {
	cyrb53,
	duplicateVar,
	formatFileSize,
	formatMoney,
	getAxiosErrorMessage,
	getShippingOrigin,
	getOS,
	getDeviceType,
	getBrowser,
	isValidEmail,
	isValidPhone,
} from '@/lib/helper';
import shipping from '@/api/shipping';
import {
	INSTANT_PAYMENT_FEE,
	INSTANT_PAYMENT_FEE_TAX,
	paymentChannels,
} from '@/lib/statics';
import productApi from '@/api/products';
import submissionApi from '@/api/submissions';

export default {
	components: {
		MainFooter,
		DragAndDrop,
		Breadcrumb,
		Shimmer,
		PrivateOrderWholesale,
	},
	data() {
		return {
			subdistrictKeyword: '',
			selectedSubdistrict: null,
			subdistricts: [],
			isLoadingSubdistrics: false,
			paymentChannels: duplicateVar(paymentChannels),
			orderType: 'bulky',
			selectedPaymentChannel: 'bank_transfer',
			isLoading: false,
			isSubmit: false,
			product: null,
			productSlug: this.$route.params?.slug ?? null,
			quantity: 1,
			instantPaymentFee: INSTANT_PAYMENT_FEE,
			instantPaymentFeeTax: INSTANT_PAYMENT_FEE_TAX,
			name: '',
			phone: '',
			email: '',
			address: '',
			shippingLabel: null,
			errorMessages: {
				name: '',
				email: '',
				phone: '',
				subdistrict: '',
				address: '',
				shippingLabel: '',
			},
			// Improvement: create meta generator component that emit meta json
			ip: null,
			location: '',
			position: null,
			latitude: 0,
			longitude: 0,
			isLocating: false,
			locationData: null,
			isLocationFetched: true,
			orderTypes: [
				{
					type: 'bulky',
					label: 'Bulky',
					description: 'Jenis order yang memungkinkan pembelian dalam jumlah besar',
					icon: 'bi bi-boxes',
				},
				{
					type: 'outsource',
					label: 'Outsource',
					description: 'Jenis order yang resinya berasal dari luar OStock (Marketplace/Agregator)',
					icon: 'bi bi-box',
				},
			],
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
			isHasFeature: 'isHasFeature',
			isDropshipper: 'isDropshipper',
		}),
		breadcrumbPaths() {
			return [
				{
					title: 'Home',
					url: '/',
				},
				{
					title: 'Wholesale Order',
					url: '/wholesale-order',
				},
				{
					title: 'Product Detail',
					url: `/wholesale-order/${this.productSlug}`,
				},
			];
		},
		productImage() {
			let productImageUrl = 'https://cdn.ostock.id/img/default-product.png';
			if (this.product?.pictures && this.product?.pictures?.length > 0) {
				productImageUrl = this.product.pictures[0];
			}
			return {
				backgroundImage: `url(${productImageUrl})`,
			};
		},
		wholesaleData() {
			const chunkSize = 3;
			const wholesale = [];

			const wholesales = this.getDistributorWholesalePrice(this.product);

			for (let i = 0; i < wholesales.length; i += chunkSize) {
				wholesale.push(wholesales.slice(i, i + chunkSize));
			}

			return wholesale;
		},
		shippingLabelPrefix() {
			let filePrefix = '';
			let sellerName = this.user?.name ?? '';
			if (this.product?.name?.length > 0) {
				const productName = this.product.name.replaceAll(' ', '_');
				filePrefix = `${productName}`;
			}
			if (this.name?.length > 0) {
				sellerName = this.name.replaceAll(' ', '_');
			}
			if (filePrefix?.length > 0) {
				filePrefix += `-${sellerName}`;
			} else {
				filePrefix += `${sellerName}`;
			}
			return filePrefix;
		},
		availablePaymentChannels() {
			const paymentChannels = [
				{ key: 'bank_transfer', label: 'Transfer Bank' },
				{ key: 'instant', label: 'Instant' },
			];
			return paymentChannels;
		},
		isOrderTypeBulky() {
			return this.orderType === 'bulky';
		},
		isOrderTypeOutsource() {
			return this.orderType === 'outsource';
		},
		isPaymentMethodInstant() {
			return this.selectedPaymentChannel === 'instant';
		},
		isIncludeOtherFee() {
			return this.isOrderTypeOutsource && this.isPaymentMethodInstant;
		},
		selectedPrice() {
			let price = 0;
			const wholesale = this.getDistributorWholesalePrice(this.product);
			const firstWholesale = wholesale[0] ?? {};
			if (this.quantity <= 1) {
				price = firstWholesale?.price ?? 0;
			} else {
				const selectedWholesale = wholesale.find(item => Number(item.min) <= this.quantity && this.quantity <= Number(item.max));
				if (selectedWholesale) {
					price = selectedWholesale?.price ?? 0;
				} else if (this.quantity > 1) {
					const lastWholesale = wholesale.at(-1);
					if (lastWholesale) {
						price = lastWholesale?.price ?? 0;
					}
				}
			}
			return price;
		},
		totalProductPrice() {
			return this.quantity * this.selectedPrice;
		},
		totalPrice() {
			let grandTotal = this.totalProductPrice;
			if (this.isIncludeOtherFee) {
				grandTotal += this.instantPaymentFee + this.instantPaymentFeeTax;
			}
			return grandTotal;
		},
		meta() {
			const os = getOS();
			const deviceType = getDeviceType();
			const browser = getBrowser();
			const location = this.location;
			const userAgent = window.navigator.userAgent;
			const meta = {
				ip: this.ip,
				os,
				device: deviceType,
				browser,
				location,
				lat: this.latitude,
				long: this.longitude,
				user_agent: userAgent,
			};
			return meta;
		},
		shippingLabelSize() {
			const fileSize = this.shippingLabel?.size ?? 0;
			return formatFileSize(fileSize);
		},
	},
	methods: {
		getIconClass(item) {
			let classes = 'fs-24';
			const icon = item?.icon ?? '';
			classes += ` ${icon}`;
			return classes;
		},
		validateName() {
			this.errorMessages.name = '';
			if (this.name.length <= 0) {
				this.errorMessages.name = 'Nama Seller wajib diisi';
			}
		},
		validateEmail() {
			this.errorMessages.email = '';
			if (this.email.length <= 0) {
				this.errorMessages.email = 'Email wajib diisi';
			} else if (!isValidEmail(this.email)) {
				this.errorMessages.email = 'Email tidak valid';
			}
		},
		validatePhone() {
			this.errorMessages.phone = '';
			const validPhone = isValidPhone(this.phone);
			this.phone = this.phone.replace(/[^\d]/g, '');
			if (validPhone) {
				const firstSelection = this.phone.substring(0, 1);
				const secondSelection = this.phone.substring(0, 2);
				let phone = this.phone;
				if (secondSelection === '62') {
					phone = phone.replace('62', '');
				} else if (firstSelection === '0') {
					phone = phone.replace('0', '');
				}
				this.phone = phone;
				if (this.phone.length < 9) {
					this.errorMessages.phone = 'Nomor Telepon Minimal 9 Digit';
				}
			} else {
				this.errorMessages.phone = 'Nomor Telepon Tidak Valid';
			}
		},
		validateSubdistrict() {
			this.errorMessages.subdistrict = '';
			if (this.selectedSubdistrict === null) {
				this.errorMessages.subdistrict = 'Kota / Kecamatan wajib dipilih';
			}
		},
		validateAddress() {
			this.errorMessages.address = '';
			if (this.address.length <= 0) {
				this.errorMessages.address = 'Alamat wajib diisi';
			}
		},
		validateShippingLabel() {
			this.errorMessages.shippingLabel = '';
			if (this.shippingLabel === null) {
				this.errorMessages.shippingLabel = 'Shipping Label wajib diupload';
			}
		},
		onInputName() {
			this.validateName();
		},
		onInputEmail() {
			this.validateEmail();
		},
		onInputPhone() {
			this.validatePhone();
		},
		onCloseSubdistrict() {
			this.validateSubdistrict();
		},
		onInputAddress() {
			this.validateAddress();
		},
		resetValidation() {
			this.errorMessages = {
				name: '',
				email: '',
				phone: '',
				subdistrict: '',
				address: '',
				shippingLabel: '',
			};
		},
		validateAllForms() {
			this.resetValidation();

			this.validateName();
			this.validateEmail();
			this.validatePhone();

			if (this.isOrderTypeBulky) {
				this.validateSubdistrict();
				this.validateAddress();
			} else if (this.isOrderTypeOutsource) {
				this.validateShippingLabel();
			}
		},
		submit() {
			this.validateAllForms();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isSubmit = true;

			const customerData = {
				name: this.name,
				phone: `+62${this.phone}`,
				email: this.email,
				notes: '',
				quantity: Number(this.quantity),
			};

			const metaJson = JSON.stringify(this.meta);

			const payload = {
				order_type: this.orderType,
				product_id: this.product?._id?.$oid ?? this.product?._id,
				payment_method: this.selectedPaymentChannel,
				meta: metaJson,
			};

			if (this.isOrderTypeBulky) {
				customerData.address = this.address;
				customerData.state = this.selectedSubdistrict.province_name;
				customerData.city = this.selectedSubdistrict.city_name_with_type;
				customerData.district = this.selectedSubdistrict.subdistrict_name;
				customerData.province_id = this.selectedSubdistrict.province_id;
				customerData.province_name = this.selectedSubdistrict.province_name;
				customerData.city_id = this.selectedSubdistrict.city_id;
				customerData.city_name = this.selectedSubdistrict.city_name;
				customerData.subdistrict_id = this.selectedSubdistrict.subdistrict_id;
				customerData.subdistrict_name = this.selectedSubdistrict.subdistrict_name;
				customerData.zip = this.selectedSubdistrict.zip;
			} else if (this.isOrderTypeOutsource) {
				payload.shipping_label = this.shippingLabel?.url ?? '';
			}

			payload.customer_data = JSON.stringify(customerData);

			let stringToHash = '';
			// eslint-disable-next-line no-restricted-syntax
			for (const key in customerData) {
				// eslint-disable-next-line no-prototype-builtins
				if (customerData.hasOwnProperty(key)) {
					const element = customerData[key];
					stringToHash += String(element);
				}
			}
			const hashCode = cyrb53(stringToHash);
			payload.hash = hashCode;

			const callback = (response) => {
				const result = response.data;
				this.toThankYouPage(result);
				this.isSubmit = false;
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
				this.isSubmit = false;
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Wholesale Order Checkout',
					text: message || 'Something went wrong',
				});
			};

			submissionApi.createWholesaleOrder(payload, callback, errorCallback);
		},
		toThankYouPage(result) {
			const pid = result?.private_order_id ?? '';
			this.$router.push({
				name: 'thank-you',
				params: { slug: this.productSlug },
				query: { pid },
			});
		},
		setUrl(value) {
			this.shippingLabel = value;
			this.validateShippingLabel();
		},
		setUserData() {
			if (this.user) {
				this.name = this.user.name;
				this.phone = this.user.phone;
				this.email = this.user.email;
				this.address = this.user.store?.address ?? '';
				const origin = this.user?.store?.shipping?.origin;
				origin.province_id = origin.province.id;
				origin.province_name = origin.province.name;
				origin.city_id = origin.city.id;
				origin.city_name = origin.city.name;
				origin.city_name_with_type = origin.city.name;
				origin.subdistrict_id = origin.subdistrict.id;
				origin.subdistrict_name = origin.subdistrict.name;
				this.selectedSubdistrict = origin;
			}
		},
		formatMoney,
		getShippingOrigin,
		stockLabel(product) {
			const stock = this.getDistributorStock(product);
			return stock < 100 ? 'Kurang dari 100' : stock;
		},
		getDistributorStock(product) {
			return product?.distributor?.stock?.quantity ?? 0;
		},
		getDistributorWholesalePrice(product) {
			return product?.distributor?.wholesale ?? [];
		},
		getPaymentChannelIcon(key) {
			const iconUrl = `https://cdn.ostock.id/img/payment-${key}.png`;
			return iconUrl;
		},
		// eslint-disable-next-line camelcase
		cityLabel({ province_name, city_name_with_type, subdistrict_name }) {
			// eslint-disable-next-line camelcase
			return `${province_name}, ${city_name_with_type}, ${subdistrict_name}`;
		},
		fetchSubdistricts(query) {
			if (query.length >= 3) {
				this.subdistrictKeyword = query;
				this.getSubdistricts();
			}
		},
		getSubdistricts() {
			this.isLoadingSubdistrics = true;
			const subdistrictIds = JSON.stringify(this.originIds);
			const params = {
				page: 1,
				subdistrict_ids: subdistrictIds,
				get_complete_data: true,
				keyword: this.subdistrictKeyword,
			};
			const callback = (response) => {
				const subdistricts = response.data;
				this.subdistricts = subdistricts;
				this.isLoadingSubdistrics = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Get Subdistricts',
					text: message,
				});
				this.isLoadingSubdistrics = false;
			};
			shipping.getCompleteSubdistricts(params, callback, errorCallback);
		},
		getProductBySlug(slugName) {
			this.isLoading = true;

			const cb = (resp) => {
				this.isLoading = false;
				this.product = resp.data ?? null;
			};

			const errorCb = () => {
				this.isLoading = false;
				this.$notify({
					group: 'products',
					text: 'Product Not found',
					type: 'error',
				});
			};

			productApi.getPrivateProductDetail(slugName, cb, errorCb);
		},
		back() {
			this.$router.go(-1);
		},
		getIp() {
			const endpoint = 'https://public.orderonline.id/ip';
			Axios.get(endpoint)
				.then((response) => {
					const ip = response.data.ip;
					this.ip = ip;
				})
				.catch(() => {
					this.isLocationFetched = true;
				});
		},
		getLocation() {
			this.isLocating = true;
			if (navigator.geolocation) {
				const onSuccess = (pos) => {
					this.position = duplicateVar(pos);
					const lat = pos.coords.latitude;
					const long = pos.coords.longitude;
					this.latitude = lat;
					this.longitude = long;
					this.isLocating = false;
				};
				const onError = () => {
					this.isLocating = false;
				};
				navigator.geolocation.getCurrentPosition(onSuccess, onError);
			} else {
				this.isLocating = false;
			}
		},
		checkFeature() {
			if (this.user) {
				const isAllowed = this.isDropshipper;
				if (!isAllowed) {
					this.$router.replace('/');
				}
			}
		},
		validateQuantityLimit(qty) {
			qty = Number(qty);
			if (qty <= 1) {
				qty = 1;
			} else if (qty >= 999999) {
				qty = 999999;
			}
			return qty;
		},
		updateQuantity(qty) {
			qty = this.validateQuantityLimit(qty);
			this.quantity = qty;
		},
		checkQuantityQuery() {
			const qty = this.$route.query?.qty ?? 1;
			this.updateQuantity(qty);
		},
	},
	watch: {
		user() {
			this.checkFeature();
		},
		quantity(value) {
			this.updateQuantity(value);
		},
		ip() {
			if (this.ip) {
				const geoApiKey = process.env.VUE_APP_GEODB_API_KEY;
				const endpoint = `https://geolocation-db.com/json/${geoApiKey}/${this.ip}`;
				Axios.get(endpoint)
					.then(({ data }) => {
						this.locationData = data;
						const city = data.city || 'Unknown city';
						const country = data.country_name || 'Unknown country';
						const location = `${city}, ${country}`;
						this.location = location;
						this.isLocationFetched = true;
					})
					.catch(() => {
						this.isLocationFetched = true;
					});
			}
		},
	},
	created() {
		this.checkFeature();
		this.getIp();
		this.getLocation();
		this.getProductBySlug(this.productSlug);
		this.setUserData();
		this.checkQuantityQuery();
	},
};
</script>

<style scoped lang="scss">
.title {
	font-weight: 700;
	color: black;
}

.first-aggregate {
	list-style: none;
}

.price-color {
	color: #ff9f1a;
}

.radius-0 {
	border-top-left-radius: 0 !important;
	border-top-right-radius: 0 !important;
	border-bottom-left-radius: 0 !important;
	border-bottom-right-radius: 0 !important;
}

.qty-minus, .qty-plus {
	background: #f1f1f1;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}

.card {
	.form-control {
		background-color: #fff !important;
	}
}

.product-image {
	width: 108px;
	height: 108px;
}
@media only screen and (max-width: 600px) {
	.product-image {
		width: 300px;
		height: 300px;
	}
}

.shipping-info {
	&-title {
		font-size: 16px;
	}

	&-description {
		font-size: 14px;
	}
}

.product-name {
	font-size: 18px;
	font-weight: 700;
}

.sticky-payment-section {
	position: sticky;
	top: 75px;
}

.grand-total {
	font-size: 20px;
	font-weight: 600;
}
</style>
